import { format, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useAppContext } from 'src/contexts/AppContext';
import {
  Content,
  getContent,
  useContent,
  useContentContext,
  useFormattedContent,
} from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { PosCurrencyField } from 'src/core/POS/PosCurrencyField';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { getTextFieldState, PosTextField } from 'src/core/POS/PosTextField';
import { vars } from 'src/core/themes';
import { Radio, RadioGroup, Stack, TooltipPopover } from 'src/core/ui';
import { InfoOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { FLIGHT_DURATION_TO_CID } from 'src/utils/constants/contentIdMaps';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import { applyNumberFormatting } from 'src/utils/numberFormatter';
import { posFieldFromValues } from 'src/utils/posFieldUtils';
import {
  BiddingMode,
  Event,
  FlightDuration,
  ListingMetrics,
  SponsoredListingsCampaign,
  SponsoredListingsCampaignInput,
} from 'src/WebApiController';

import {
  DEFAULT_BASE_BID,
  getProspectiveBudget,
  MINIMUM_BASE_BID,
} from '../sponsoredListings.util';
import * as styles from './CampaignModal.css';

export const CampaignModalBodyV2 = ({
  campaign,
  event,
  disabled,
  eventNameDisplay,
  metrics,
}: {
  campaign?: SponsoredListingsCampaign | null;
  event: Event;
  disabled: boolean;
  eventNameDisplay: string;
  metrics?: ListingMetrics;
}) => {
  const { isDarkMode } = useSiteTheme();
  const { loginContext } = useAppContext();
  const { getUiCurrency } = useLocalizationContext();
  const [hasManualNameChanged, setHasManualNameChanged] = useState(false);
  const [hasManualBudgetChanged, setHasManualBudgetChanged] = useState(false);
  const uiCurrency = useMemo(
    () => getUiCurrency(loginContext?.user?.activeAccount?.currencyCode),
    [getUiCurrency, loginContext]
  );

  const contentContext = useContentContext();

  const {
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext<SponsoredListingsCampaignInput>();

  const campaignNamePlaceholder = useContent(ContentId.EnterAdCampaignName);

  const campaignId = watch('campaignId');
  const campaignName = watch('campaignName');
  const flightDuration = watch('flightDuration');
  const biddingMode = watch('biddingMode');
  const baseBid = watch('baseBid');
  const recurringFlightBudget = watch('recurringFlightBudget');
  const currentFlightBudget = watch('currentFlightBudget');
  const targetTicketClassIds = watch('targetTicketClassIds');

  const untilLabel = useContent(ContentId.Until);

  const campaignEndDateUtc = useMemo(
    () =>
      campaign?.activeFlight?.dateRange?.end
        ? toZonedTime(parseISO(campaign.activeFlight.dateRange.end!), 'UTC')
        : null,
    [campaign?.activeFlight?.dateRange?.end]
  );

  const requiredText = useContent(ContentId.Required);
  const currentFlightBudgetLessThanSpendText = useContent(
    ContentId.CurrentFlightBudgetLessThanSpendError
  );
  const flightDurationText = useContent(
    FLIGHT_DURATION_TO_CID[flightDuration.value ?? FlightDuration.Daily]
  );
  const baseBidExceedsBudgetText = useFormattedContent(
    FormatContentId.BaseBidExceedsBudgetError,
    [`${flightDurationText}`]
  );
  const budgetMustBeGreaterThanBaseBidText = useContent(
    ContentId.BudgetMustBeGreaterThanBaseBidError
  );

  return (
    <Stack
      direction="column"
      gap="l"
      width="full"
      className={styles.boostBodyContainer}
    >
      <Stack direction="column" gap="m" width="full">
        <Stack gap="l" width="full">
          <Stack
            gap="m"
            alignItems="center"
            width="fit"
            className={styles.itemLabel}
          >
            <span>1.</span>
            <Content id={ContentId.CampaignName} />
          </Stack>
          <Stack width="full">
            <PosFormField errors={errors.campaignName?.message}>
              <PosTextField
                disabled={disabled}
                errorState={getTextFieldState(errors.campaignName)}
                value={campaignName?.value ?? ''}
                placeholder={campaignNamePlaceholder}
                type="text"
                inputMode="text"
                onChange={(e) => {
                  setHasManualNameChanged(true);
                  setValue(
                    'campaignName',
                    posFieldFromValues(e.target.value, campaign?.campaignName)
                  );
                  clearErrors('campaignName');
                }}
                onBlur={() => {
                  if (
                    !campaignName.value ||
                    campaignName.value.trim().length === 0
                  ) {
                    setError('campaignName', {
                      type: 'required',
                      message: requiredText,
                    });
                  }
                }}
              />
            </PosFormField>
          </Stack>
        </Stack>
      </Stack>
      <Stack direction="column" gap="m" width="full">
        <Stack gap="m" alignItems="center" className={styles.itemLabel}>
          <span>2.</span>
          <Content id={ContentId.Bidding} />
        </Stack>
        <RadioGroup
          value={biddingMode.value}
          disabled={disabled}
          onValueChange={(v: BiddingMode) => {
            setValue(
              'biddingMode',
              posFieldFromValues(v, campaign?.biddingMode)
            );

            if (v === BiddingMode.Manual && baseBid?.value == null) {
              setValue(
                'baseBid',
                posFieldFromValues(DEFAULT_BASE_BID, campaign?.baseBid)
              );
            }
          }}
        >
          <Radio
            disabled={disabled}
            value={BiddingMode.Automatic}
            variant="outline"
            label={<Content id={ContentId.Automatic} />}
            labelSubtext={
              <Content id={ContentId.Automatic_Bidding_Description} />
            }
          />
          <Radio
            disabled={disabled}
            value={BiddingMode.Manual}
            variant="outline"
            label={<Content id={ContentId.Manual} />}
            labelSubtext={<Content id={ContentId.Manual_Bidding_Description} />}
          />
        </RadioGroup>
      </Stack>
      <Stack
        direction="column"
        gap="m"
        width="full"
        className={styles.subsectionContainer}
      >
        {biddingMode.value === BiddingMode.Manual && (
          <Stack gap="l" width="full">
            <Stack gap="l" width="half">
              <Stack
                gap="m"
                alignItems="center"
                width="full"
                className={styles.itemLabel}
              >
                <span>
                  <Content id={ContentId.SetBidAmount} />
                </span>
                <TooltipPopover
                  contentVariant={isDarkMode ? 'light' : 'dark'}
                  triggerContent={
                    <InfoOutlineIcon
                      withHoverEffect
                      fill={vars.color.iconPrimary}
                      size={vars.iconSize.s}
                    />
                  }
                >
                  <Content id={ContentId.SetBidAmountDescription} />
                </TooltipPopover>
              </Stack>
            </Stack>
            <Stack width="half">
              <PosFormField errors={errors.baseBid?.message}>
                <PosCurrencyField
                  disabled={disabled}
                  errorState={getTextFieldState(errors.baseBid)}
                  value={baseBid?.value ?? ''}
                  onChange={(e) => {
                    let newNumber = e.target.value
                      ? Number(e.target.value)
                      : null;

                    if (typeof newNumber !== 'number' || isNaN(newNumber)) {
                      newNumber = null;
                    }
                    setHasManualBudgetChanged(true);
                    setValue(
                      'baseBid',
                      posFieldFromValues(newNumber, campaign?.baseBid)
                    );
                    clearErrors('baseBid');
                  }}
                  uiCurrency={uiCurrency}
                  onBlur={() => {
                    // Validate if manual bidding is selected
                    if (biddingMode.value === BiddingMode.Manual) {
                      if (baseBid.value == null) {
                        setError('baseBid', {
                          type: 'required',
                          message: requiredText,
                        });
                      } else if (baseBid.value < MINIMUM_BASE_BID) {
                        setValue(
                          'baseBid',
                          posFieldFromValues(
                            MINIMUM_BASE_BID,
                            campaign?.baseBid
                          )
                        );
                      }
                    }
                    // Validate that the base bid cannot be more than the recurring flight budget
                    if (
                      biddingMode.value === BiddingMode.Manual &&
                      baseBid.value &&
                      // Base bid cannot exceed either the recurring budget or current flight budget (if editing active flight)
                      (baseBid.value > (recurringFlightBudget.value ?? 0) ||
                        (currentFlightBudget.value &&
                          baseBid.value > currentFlightBudget.value))
                    ) {
                      setError('baseBid', {
                        type: 'max',
                        message: baseBidExceedsBudgetText as string,
                      });
                    } else {
                      clearErrors('recurringFlightBudget');
                      clearErrors('currentFlightBudget');
                    }
                  }}
                />
              </PosFormField>
            </Stack>
          </Stack>
        )}
        {campaign?.activeFlight && (
          <Stack gap="l" width="full">
            <Stack direction="column" width="half">
              <Stack className={styles.itemLabel} gap="m" alignItems="center">
                <span>
                  <Content id={ContentId.ActiveBudget} />{' '}
                  <span>
                    {campaignEndDateUtc &&
                      `(${format(campaignEndDateUtc, 'M/d')})`}
                  </span>
                </span>
                <TooltipPopover
                  contentVariant={isDarkMode ? 'light' : 'dark'}
                  triggerContent={
                    <InfoOutlineIcon
                      withHoverEffect
                      fill={vars.color.iconPrimary}
                      size={vars.iconSize.s}
                    />
                  }
                >
                  <span>
                    {campaignEndDateUtc &&
                      `(${untilLabel} ${format(campaignEndDateUtc, 'M/d/yyyy @ h:mm aa ')} UTC)`}
                  </span>
                </TooltipPopover>
              </Stack>
              <span className={styles.information}>
                <Content id={ContentId.AlreadySpent} />:{' '}
                {applyNumberFormatting({
                  inputNumber: campaign.activeFlight.flightSpend,
                  currencyCode: uiCurrency.code,
                  currencyDecimalDigits: uiCurrency.dec,
                })}
              </span>
            </Stack>
            <Stack width="half">
              <PosFormField errors={errors.currentFlightBudget?.message}>
                <PosCurrencyField
                  disabled={disabled}
                  errorState={getTextFieldState(errors.currentFlightBudget)}
                  value={currentFlightBudget?.value ?? ''}
                  onChange={(e) => {
                    let newNumber = e.target.value
                      ? Number(e.target.value)
                      : null;

                    if (typeof newNumber !== 'number' || isNaN(newNumber)) {
                      newNumber = null;
                    }
                    setHasManualBudgetChanged(true);
                    setValue(
                      'currentFlightBudget',
                      posFieldFromValues(
                        newNumber,
                        campaign.activeFlight?.flightBudget
                      )
                    );
                    clearErrors('currentFlightBudget');
                  }}
                  uiCurrency={uiCurrency}
                  onBlur={() => {
                    // Validate the current flight budget has to be greater than spend
                    if (
                      campaign?.activeFlight &&
                      (currentFlightBudget.value || 0) <
                        (campaign.activeFlight.flightSpend || 0)
                    ) {
                      setError('currentFlightBudget', {
                        type: 'min',
                        message: currentFlightBudgetLessThanSpendText,
                      });
                    }

                    // Validate that the base bid cannot be more than the recurring flight budget
                    if (
                      biddingMode.value === BiddingMode.Manual &&
                      baseBid.value &&
                      // Base bid cannot exceed either the recurring budget or current flight budget (if editing active flight)
                      (baseBid.value > (recurringFlightBudget.value ?? 0) ||
                        (currentFlightBudget.value &&
                          baseBid.value > currentFlightBudget.value))
                    ) {
                      setError('currentFlightBudget', {
                        type: 'max',
                        message: budgetMustBeGreaterThanBaseBidText as string,
                      });
                    } else {
                      clearErrors('baseBid');
                    }
                  }}
                />
              </PosFormField>
            </Stack>
          </Stack>
        )}
        <Stack gap="l" width="full">
          <Stack gap="l" width="half">
            <Stack
              gap="m"
              alignItems="center"
              width="full"
              className={styles.itemLabel}
            >
              <PosEnumSelect
                className={styles.itemLabel}
                fontWeight={400}
                disabled={disabled}
                variant="textPlain"
                value={flightDuration?.value ?? FlightDuration.Daily}
                valueOptionsContent={FLIGHT_DURATION_TO_CID}
                sortMode="none"
                displayText={`${campaignId > 0 ? getContent(ContentId.Future, contentContext) : ''} ${getContent(FLIGHT_DURATION_TO_CID[flightDuration?.value ?? FlightDuration.Daily], contentContext)}`}
                onChange={(option: FlightDuration | null) => {
                  if (option) {
                    setValue(
                      'flightDuration',
                      posFieldFromValues(option, campaign?.flightDuration)
                    );

                    // If new campaign, apply auto-generation of names and budget logic
                    if (!hasManualNameChanged) {
                      setValue(
                        'campaignName',
                        posFieldFromValues(
                          `${eventNameDisplay} - ${option}`,
                          campaign?.campaignName
                        )
                      );
                    }
                    if (!hasManualBudgetChanged) {
                      const newBudget = getProspectiveBudget(
                        metrics?.unsoldCst,
                        option,
                        event.dates.start
                      );
                      if (newBudget != null) {
                        setValue(
                          'recurringFlightBudget',
                          posFieldFromValues(
                            newBudget,
                            campaign?.recurringFlightBudget
                          )
                        );
                      } else {
                        clearErrors('baseBid');
                      }
                    }
                  }
                }}
              />
              <TooltipPopover
                contentVariant={isDarkMode ? 'light' : 'dark'}
                triggerContent={
                  <InfoOutlineIcon
                    withHoverEffect
                    fill={vars.color.iconPrimary}
                    size={vars.iconSize.s}
                  />
                }
              >
                <Content id={ContentId.FlightBudgetDurationHelpInfo} />
              </TooltipPopover>
            </Stack>
          </Stack>
          <Stack width="half">
            <PosFormField errors={errors.recurringFlightBudget?.message}>
              <PosCurrencyField
                disabled={disabled}
                errorState={getTextFieldState(errors.recurringFlightBudget)}
                value={recurringFlightBudget?.value ?? ''}
                onChange={(e) => {
                  let newNumber = e.target.value
                    ? Number(e.target.value)
                    : null;

                  if (typeof newNumber !== 'number' || isNaN(newNumber)) {
                    newNumber = null;
                  }

                  setHasManualBudgetChanged(true);
                  setValue(
                    'recurringFlightBudget',
                    posFieldFromValues(
                      newNumber,
                      campaign?.recurringFlightBudget
                    )
                  );
                  clearErrors('recurringFlightBudget');
                }}
                uiCurrency={uiCurrency}
                onBlur={() => {
                  // Validate budget
                  if (
                    !recurringFlightBudget.value ||
                    recurringFlightBudget.value <= 0
                  ) {
                    setError('recurringFlightBudget', {
                      type: 'required',
                      message: requiredText,
                    });
                  }

                  // Validate that the base bid cannot be more than the recurring flight budget
                  if (
                    biddingMode.value === BiddingMode.Manual &&
                    baseBid.value &&
                    // Base bid cannot exceed either the recurring budget or current flight budget (if editing active flight)
                    (baseBid.value > (recurringFlightBudget.value ?? 0) ||
                      (currentFlightBudget.value &&
                        baseBid.value > currentFlightBudget.value))
                  ) {
                    setError('recurringFlightBudget', {
                      type: 'max',
                      message: budgetMustBeGreaterThanBaseBidText as string,
                    });
                  }
                }}
              />
            </PosFormField>
          </Stack>
        </Stack>
      </Stack>

      <Stack direction="column" gap="m" width="full">
        <Stack gap="m" alignItems="center" className={styles.itemLabel}>
          <span>3.</span>
          <Content id={ContentId.Target} />
          <TooltipPopover
            contentVariant={isDarkMode ? 'light' : 'dark'}
            triggerContent={
              <InfoOutlineIcon
                withHoverEffect
                fill={vars.color.iconPrimary}
                size={vars.iconSize.s}
              />
            }
          >
            <Content id={ContentId.CampaignTargetHelpInfo} />
          </TooltipPopover>
        </Stack>
        <RadioGroup
          disabled={disabled}
          value={targetTicketClassIds?.value?.length ? 'zone' : 'default'}
          onValueChange={(value) => {
            console.log('Unsupported campaign target: ', value);
          }}
        >
          <Radio
            disabled={disabled}
            value="default"
            variant="outline"
            label={<Content id={ContentId.AllZones} />}
            rightContent={
              <span className={styles.itemMetadata}>
                <Content id={ContentId.Recommended} />
              </span>
            }
          />
        </RadioGroup>
      </Stack>
    </Stack>
  );
};
